<template>
  <div>
    <div v-if="loading"><dsa-loading text="Loading dashboard…" /></div>
    <template v-else>
      <div class="welcome">
        <div>
          <h1>{{ accountName }}</h1>
          <p class="sub">Manage your institution's DSA subjects and monitor student exam activity.</p>
        </div>
        <span class="badge">{{ quarterLabel }} · {{ quota.used }} of {{ quota.limit }} subjects enrolled</span>
      </div>

      <div v-for="b in bulletins" :key="'b-' + b.id" class="notice" :class="{ warn: b.type === 'warning' }">
        <b v-if="b.title">{{ b.title }}:</b> {{ b.message }}
      </div>

      <div class="stats four">
        <div class="card">
          <div class="label">Quarterly subject quota</div>
          <div class="value">{{ quota.used }} <small style="font-size:15px;color:var(--muted)">of {{ quota.limit }} used</small></div>
          <div class="bar"><i :style="{ width: quotaPercent + '%' }"></i></div>
          <div class="hint">{{ quota.remaining }} subjects remaining for {{ quarterLabel }}</div>
        </div>
        <div class="card">
          <div class="label">Students registered</div>
          <div class="value">{{ registeredStudentCount }}</div>
          <div class="hint">Across {{ subjects.length }} enrolled subjects</div>
        </div>
        <div class="card">
          <div class="label">Registrations this quarter</div>
          <div class="value">{{ summary.total }}</div>
          <div class="hint">{{ summary.exam_ready }} exam ready · {{ summary.scored }} scored</div>
        </div>
        <div class="card">
          <div class="label">Pass not redeemed</div>
          <div class="value">{{ summary.pass_pending }}</div>
          <div class="hint">Students awaiting Pass redemption</div>
        </div>
      </div>

      <div v-if="summary.pass_pending > 0" class="notice warn">
        <b>{{ summary.pass_pending }} registration(s)</b> still need Pass redemption. Students must redeem a Pass before taking the exam.
      </div>

      <div class="notice">
        <b>Quota rules:</b> subject enrollment locks your quarterly quota — applications cannot be cancelled or swapped once submitted.
        {{ quota.remaining }} of {{ quota.limit }} subject slots remain for {{ quarterLabel }}.
      </div>

      <div class="section">
        <div class="section-head">
          <h2>{{ quarterLabel }} enrolled subjects</h2>
          <div class="actions">
            <router-link to="/institution/catalog">Enroll subjects</router-link>
            <router-link to="/institution/registrations">View registrations</router-link>
          </div>
        </div>
        <div v-if="!subjects.length" class="empty">
          <h3>No subjects enrolled yet</h3>
          <p>Enroll up to {{ quota.limit }} subjects from the catalog for this quarter.</p>
          <div class="actions" style="justify-content:center">
            <router-link to="/institution/catalog" class="btn btn-primary">Browse subject catalog</router-link>
          </div>
        </div>
        <table v-else>
          <thead><tr><th>Subject</th><th>Registrations</th><th>Status</th><th style="text-align:right">Actions</th></tr></thead>
          <tbody>
            <tr v-for="sub in subjects" :key="sub.subject_code">
              <td><span class="subject">{{ sub.subject_name }}<small>{{ subjectCategory(sub) }}</small></span></td>
              <td>{{ sub.registered_students || 0 }}</td>
              <td>
                <span class="chip" :class="sub.registration_open ? 'open' : 'pend'">
                  {{ sub.status_label || (sub.registration_open ? 'Enrollment open' : 'Closed') }}
                </span>
              </td>
              <td class="td-actions" style="text-align:right">
                <router-link
                  :to="{ path: '/institution/registrations', query: { subject: sub.subject_code } }"
                  class="entry"
                >Registrations</router-link>
                <button type="button" class="entry" @click="openRegistrationLink(sub)">Registration Link</button>
                <button type="button" class="entry" @click="openPoster(sub)">Poster</button>
                <router-link
                  :to="`/institution/past-quarters/${sub.subject_code}`"
                  class="entry"
                >History</router-link>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <div v-if="linkModal" class="pm-overlay" @click.self="linkModal = false">
      <div class="pm-box link-box">
        <div class="pm-head">
          <div>
            <h3>Student Registration Link</h3>
            <p>Share this institution-specific form for {{ linkSubject && linkSubject.subject_name }}.</p>
          </div>
          <button type="button" class="pm-x" @click="linkModal = false">×</button>
        </div>
        <div class="pm-body link-body">
          <div class="pm-note">
            Students who register through this link are bound to <b>{{ accountName }}</b>, create an application for
            <b>{{ linkSubject && linkSubject.subject_name }}</b> in <b>pending payment</b> status, complete the integrity
            confirmation, and can then see all subjects enrolled by your institution.
          </div>
          <label>Registration form URL</label>
          <input class="pm-link" :value="registrationUrl" readonly>
          <div class="pm-btns">
            <button type="button" class="btn btn-primary" @click="copyRegistrationLink">Copy link</button>
            <a class="btn btn-outline" :href="registrationUrl" target="_blank" rel="noopener noreferrer">Open form</a>
          </div>
        </div>
      </div>
    </div>

    <div v-if="posterModal" class="pm-overlay" @click.self="posterModal = false">
      <div class="pm-box">
        <div class="pm-head">
          <div>
            <h3>Subject poster</h3>
            <p>Shareable poster with your institution’s dedicated registration QR code.</p>
          </div>
          <button type="button" class="pm-x" @click="posterModal = false">×</button>
        </div>
        <div class="pm-body">
          <div class="pm-poster">
            <dsa-poster
              v-if="posterSubject"
              ref="poster"
              :institution-name="accountName"
              :subject-name="posterSubject.subject_name"
              :category="posterSubject.category || subjectCategory(posterSubject)"
              :major="posterSubject.major || posterSubject.project_product_line || ''"
              :window-label="posterSubject.window_label || quarterLabel"
              :enrollment-close="posterEnrollmentClose"
              :exam-date="posterExamDate"
              :intro="posterSubject.intro || ''"
              :registration-url="posterRegistrationUrl"
            />
          </div>
          <div class="pm-side">
            <div class="pm-note">
              The QR code opens <b>your school’s dedicated DSA registration form</b> for this subject.
              Posters are generated per institution and per subject, so registrations can be traced to this channel.
            </div>
            <label>Poster link</label>
            <input class="pm-link" :value="posterShareUrl" readonly>
            <div class="pm-btns">
              <button type="button" class="btn btn-primary" @click="savePosterImage">Save as image</button>
              <button type="button" class="btn btn-outline" @click="copyPosterLink">Copy poster link</button>
            </div>
            <div class="pm-tip">
              Save the image to share on print or social media, or copy the link to send it directly — the link opens the same poster page.
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DsaPoster from '@/components/DsaPoster.vue'
import { getInstitutionDashboard, getActiveQuarters } from '@/api/dsa'
import { getInstitutionAccountId, getInstitutionAdvisorId, getAccountName, setInstitutionAdvisorId } from '@/utils/store'
import { pickCurrentQuarter, subjectCategory, formatDate } from '@/utils/dsaFormat'
import {
  buildInstitutionRegistrationUrl,
  buildInstitutionPosterUrl,
  copyTextToClipboard,
  ensureHtml2Canvas,
} from '@/utils/institutionReg'

export default {
  name: 'InstitutionDashboard',
  components: { DsaPoster },
  data() {
    return {
      loading: true,
      quarterCode: '',
      quarterLabel: '',
      quarterMeta: null,
      quota: { limit: 6, used: 0, remaining: 6 },
      subjects: [],
      registeredStudentCount: 0,
      bulletins: [],
      summary: { total: 0, pass_pending: 0, exam_ready: 0, scored: 0, pending_payment: 0 },
      linkModal: false,
      linkSubject: null,
      posterModal: false,
      posterSubject: null,
    }
  },
  computed: {
    accountName() {
      return getAccountName() || 'Institution'
    },
    accountId() {
      return getInstitutionAccountId()
    },
    quotaPercent() {
      if (!this.quota.limit) return 0
      return Math.min(100, Math.round((this.quota.used / this.quota.limit) * 100))
    },
    registrationUrl() {
      if (!this.linkSubject) return ''
      return buildInstitutionRegistrationUrl({
        accountId: this.accountId,
        projectCode: this.linkSubject.project_code,
        advisorId: getInstitutionAdvisorId(),
      })
    },
    posterRegistrationUrl() {
      if (!this.posterSubject) return ''
      return buildInstitutionRegistrationUrl({
        accountId: this.accountId,
        projectCode: this.posterSubject.project_code,
        advisorId: getInstitutionAdvisorId(),
      })
    },
    posterEnrollmentClose() {
      const sub = this.posterSubject
      if (!sub) return ''
      if (sub.enrollment_close_label) return sub.enrollment_close_label
      if (sub.reg_close) return formatDate(sub.reg_close)
      if (this.quarterMeta && this.quarterMeta.reg_close) return formatDate(this.quarterMeta.reg_close)
      return ''
    },
    posterExamDate() {
      const sub = this.posterSubject
      if (!sub) return ''
      if (sub.exam_date) return String(sub.exam_date).replace(/^Testing\s+/i, '')
      if (this.quarterMeta && this.quarterMeta.exam_start) return formatDate(this.quarterMeta.exam_start)
      return ''
    },
    posterShareUrl() {
      if (!this.posterSubject) return ''
      return buildInstitutionPosterUrl({
        account_id: this.accountId,
        project_code: this.posterSubject.project_code,
        advisor_id: getInstitutionAdvisorId(),
      })
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    subjectCategory,
    requireProjectCode(sub) {
      if (sub && sub.project_code) return true
      this.$emit('toast', 'No project is linked for this subject yet. Ask DSA admin to create the REProject.')
      return false
    },
    openRegistrationLink(sub) {
      if (!this.requireProjectCode(sub)) return
      this.linkSubject = sub
      this.linkModal = true
    },
    openPoster(sub) {
      if (!this.requireProjectCode(sub)) return
      this.posterSubject = sub
      this.posterModal = true
    },
    async copyRegistrationLink() {
      try {
        await copyTextToClipboard(this.registrationUrl)
        this.$emit('toast', 'Registration link copied')
      } catch (e) {
        this.$emit('toast', 'Copy failed')
      }
    },
    async copyPosterLink() {
      try {
        await copyTextToClipboard(this.posterShareUrl)
        this.$emit('toast', 'Poster link copied')
      } catch (e) {
        this.$emit('toast', 'Copy failed')
      }
    },
    async savePosterImage() {
      try {
        await ensureHtml2Canvas()
        const root = this.$refs.poster && this.$refs.poster.getRootEl()
        if (!root || !window.html2canvas) {
          this.$emit('toast', 'Image library not loaded')
          return
        }
        const canvas = await window.html2canvas(root, { scale: 2, backgroundColor: '#fff' })
        const a = document.createElement('a')
        const slug = String((this.posterSubject && this.posterSubject.subject_name) || 'subject')
          .replace(/[^a-z0-9]+/gi, '-')
          .replace(/^-|-$/g, '')
          .toLowerCase()
        a.download = `${slug}-dsa-poster.png`
        a.href = canvas.toDataURL('image/png')
        a.click()
        this.$emit('toast', 'Poster saved as image')
      } catch (e) {
        this.$emit('toast', 'Could not render the image')
      }
    },
    async load() {
      const account_id = getInstitutionAccountId()
      if (!account_id) return
      try {
        const qRes = await getActiveQuarters()
        const quarters = (qRes.data && qRes.data.data) || []
        const current = pickCurrentQuarter(quarters)
        this.quarterMeta = current || null
        this.quarterCode = current ? current.quarter_code : ''
        this.quarterLabel = (current && (current.quarter_name || current.quarter_code)) || 'Current quarter'
        if (!this.quarterCode) {
          this.$emit('toast', 'No active quarter configured')
          return
        }
        const res = await getInstitutionDashboard(account_id, this.quarterCode)
        if (res.data && res.data.code === 0) {
          const data = res.data.data || {}
          this.quota = data.quota || this.quota
          this.subjects = data.subjects || []
          this.registeredStudentCount = data.registered_student_count || 0
          this.bulletins = data.bulletins || []
          this.summary = data.registration_summary || this.summary
          if (data.quarter_code) this.quarterCode = data.quarter_code
          if (data.quarter_name) this.quarterLabel = data.quarter_name
          // Ensure registration/poster links include advisor even if login cookie was missing.
          if (data.primary_contact_id && !getInstitutionAdvisorId()) {
            setInstitutionAdvisorId(data.primary_contact_id)
          }
        }
      } catch (e) {
        this.$emit('toast', 'Failed to load dashboard')
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.notice.warn{background:var(--amber-bg);border-color:var(--amber-line)}
.td-actions .entry{margin-left:8px;background:none;border:none;padding:0;font:inherit;color:inherit;cursor:pointer}
.td-actions .entry:first-child{margin-left:0}
.pm-overlay{position:fixed;inset:0;background:rgba(15,23,42,.45);z-index:80;display:flex;align-items:center;justify-content:center;padding:16px}
.pm-box{background:#fff;border-radius:16px;width:min(900px,94vw);max-height:92vh;overflow:auto;box-shadow:0 24px 60px rgba(15,40,80,.25)}
.pm-box.link-box{width:min(640px,94vw)}
.pm-head{display:flex;align-items:flex-start;justify-content:space-between;padding:20px 24px;border-bottom:1px solid var(--line)}
.pm-head h3{font-size:17px;font-weight:700;margin:0}
.pm-head p{font-size:13px;color:var(--muted);margin-top:3px}
.pm-x{border:none;background:#eef1f5;color:var(--gray);width:30px;height:30px;border-radius:50%;font-size:16px;cursor:pointer;line-height:1}
.pm-x:hover{background:#e2e8f0}
.pm-body{display:flex;gap:26px;padding:24px;align-items:flex-start}
.pm-body.link-body{flex-direction:column}
.pm-poster{flex-shrink:0}
.pm-side{flex:1;min-width:220px}
.pm-note{font-size:13px;color:var(--gray);line-height:1.65;background:#F4F8FC;border:1px solid #e2ebf5;border-radius:10px;padding:12px 14px}
.pm-note b{color:var(--ink);font-weight:600}
.pm-side label,
.pm-body.link-body label{display:block;font-size:12px;color:var(--muted);margin:18px 0 6px}
.pm-link{width:100%;border:1px solid #cbd5e1;border-radius:8px;padding:9px 12px;font-size:12px;color:var(--gray);background:#fafbfd;font-family:inherit;outline:none;box-sizing:border-box}
.pm-btns{display:flex;flex-direction:column;gap:10px;margin-top:18px}
.pm-tip{font-size:12px;color:var(--muted);margin-top:16px;line-height:1.55}
@media(max-width:820px){.pm-body{flex-direction:column}.pm-poster{margin:0 auto}}
</style>
